import { CruiseSearchWidget, CustomFilterSectionType, FiltersProvider, Variant } from '@rcg/cruise-search-components';
import { useUser } from '@rcg/ui-user';
import Alerts from 'components/Alerts';
import { CruiseDetailsVariant } from 'components/CruiseDetailsPanel';
import { EVENT_CRUISE_SEARCH_APPLY_FILTERS } from 'components/CruiseDetailsPanel/CruiseDetailsPanel.constants';
import { CruisePulseWrapper } from 'components/CruisePulseWrapper/CruisePulseWrapper';
import Filters from 'components/Filters';
import { FILTERS_WRAPPER } from 'components/Filters/constants';
import filtersMap from 'components/FiltersModal/filtersMap';
import Footer from 'components/Footer';
import MainMenu from 'components/MainMenu';
import MarkerIO from 'components/MarkerIO';
import NLSearch from 'components/NLSearch';
import Promo from 'components/Promo';
import Results from 'components/Results';
import { DEFAULT } from 'components/Scroller/constants';
import SearchBar from 'components/SearchBar';
import SelectedFilters from 'components/SelectedFilters';
import { SELECTED_FILTER } from 'components/SelectedFilters/constants';
import SideBarMenu from 'components/SideBarMenu';
import { PromoBanner } from 'components/TargetedOffers/Banners';
import Loader from 'components/TargetedOffers/Components/Loader';
import ToolBar from 'components/ToolBar';
import PillButton from 'components/buttons/PillButton';
import { AnalyticsContext } from 'contexts/AnalyticsContext';
import { INITIAL_SEARCH_OPTION, TRACKING_ACTION, UTAG_VIEW } from 'contexts/AnalyticsContext/Constants';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import {
	BRAND_CEL,
	BRAND_RCI,
	CUSTOM,
	DEFAULT_FILTER_OPTIONS,
	DEPARTURE_PORT,
	DESTINATION,
	FILTER_MODE,
	PROMO,
	SAILDATE,
	SHIP,
} from 'contexts/CruiseSearchContext/constants';
import { makeStoreFromRouteQuery } from 'contexts/CruiseSearchContext/utils/filterParser';
import { getQueryFromObject } from 'contexts/CruiseSearchContext/utils/uniqueDestinationsUtils';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import { SELECTING } from 'contexts/CruiseSelectionContext/constants';
import { PersonalizationContext } from 'contexts/PersonalizationContext';
import { EMPTY_CRUISE_RECOMMENDATION_ID } from 'contexts/PersonalizationContext/constants';
import { TargetedOffersContext } from 'contexts/TargetedOffersContext';
import { useConfig } from 'hooks';
import Cookies from 'js-cookie';
import { trackOnLoadCruiseSearch } from 'lib/ui-analytics/events/onLoadCruiseSearch';
import useAutoScrollRemoval from 'lib/useAutoScrollRemoval';
import { createCookie, getCookie } from 'lib/utilities/Cookies';
import _cloneDeep from 'lodash/cloneDeep';
import dynamic from 'next/dynamic';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useFeatureFlags } from '../../hooks/LaunchDarkly/useFeatureFlags';
import { COOKIE_SEGMENT_VALIDATED, COOKIE_TD, CRUISE_SORT, PROMO_WRAPPER, RDW_ID, SEARCH_BAR, UPDATE_RESULTS_EVENT } from './Constants';
import { animateIn } from './animations';
import FilterOffers from './components/FilterOffers/FilterOffers';
import { HeaderBackground, HeaderConstrain, PageBackground, PageConstrain, PromoWrapper, SearchBarConstrain, ToolBarContainer } from './styles';
import { getRiverDeparturePorts } from './utils/riverDeparturePorts';

/**
 * Lazy Loaded Components
 */
const CruiseDetailsPanel = dynamic(() => import('components/CruiseDetailsPanel/CruiseDetailsPanel'));
const FiltersModal = dynamic(() => import('components/FiltersModal'));
const ForgotCASModal = dynamic(() => import('components/TargetedOffers/ForgotCAS'));

const searchesAllowing = [DESTINATION, SHIP, DEPARTURE_PORT];

const OffersWrapper = styled.div`
	padding: 30px 40px 0 40px;
`;

const baseCustomElement = {
	label: 'My Offers',
	key: 'myoffers',
	dictionaryLabel: 'navMyOffers',
	desktopTestId: 'my-offers-filter-button',
	mobileTestId: 'mobile-my-offers-filter-button',
	amount: 0,
	isFullColor: false,
	isSelected: false,
	customFilterConfig: {
		container: 'filter_my_offers',
		sections: [
			{
				section: CustomFilterSectionType.Default,
				labelDictionaryKey: 'labelCurrentOffers',
			},
			{
				section: CustomFilterSectionType.River,
				labelDictionaryKey: 'labelRiverOffers',
			},
		],
	},
};

const cruiseSearchWidgetCustomElements = [
	{
		...baseCustomElement,
		customElement: (props) => <FilterOffers {...props} />,
	},
];

/**
 * The parent wrapper for the search page
 */

const SearchPage = ({ router: { query } }) => {
	useAutoScrollRemoval();

	const [showUniversalNavigation, setShowUniversalNavigation] = useState(false);
	const config = useConfig();
	const { publicKeys } = config;
	const {
		globalization,
		targetedOffersLogged,
		targetedOffersPillShow,
		openFilters,
		filterOpen,
		getFavoritesList,
		additionalParamsApplied,
		locale,
		nlUserQuery,
		searchSelecting,
		results,
		loadedPromo,
		pagination,
		promo,
		modalOpen,
		defaultSorting,
		loading,
		portAdded,
		filterChange,
		updateFilterChange,
		updateInitialTrackPageEvent,
		initialTrackPageEvent,
		targetedOffersPillClosed,
		appliedDateFilter,
		brand,
		promotions,
		updateSearchSelecting,
		applyFilters,
		updateSearchSelectingFilter,
		searchApplied,
		resetSearchSelectingToSearchApplied,
		currentNls,
		nlsPerformed,
		fullBrandSettings,
		setPromotionSelected,
		promotionSelected,
		promotionApplied,
		setPromotionApplied,
		getStatePromotions,
		setPromotions,
		updateAdditionalFilters,
		filterOptions,
	} = useContext(CruiseSearchContext);
	const flags = useFeatureFlags();

	const { cruiseDetail, comparisonViewState } = useContext(CruiseSelectionContext);

	const { generalDictionary } = globalization?.dictionaries || {};

	const {
		inputOpenSearchBarPlaceholder,
		actionSortOptionRecommended,
		actionSortOptionDurationLowHigh,
		actionSortOptionDurationHighLow,
		actionSortOptionPriceLowHigh,
		actionSortOptionPriceHighLow,
		actionSortOptionDate,
	} = generalDictionary;

	const labelsSort = {
		NIGHTSASC: actionSortOptionDurationLowHigh,
		NIGHTSDESC: actionSortOptionDurationHighLow,
		PRICEASC: actionSortOptionPriceLowHigh,
		PRICEDESC: actionSortOptionPriceHighLow,
		RECOMMENDED: actionSortOptionRecommended,
		SAILDATEASC: actionSortOptionDate,
	};

	const { utagEvent, createInitialPageTrack, tracking, updateTracking, utagShowOfferPill, utagOfferPillClicked } = useContext(AnalyticsContext);

	const { closeTargetedOffersPill, updateTargetedOfferPill } = useContext(TargetedOffersContext);

	const { filtersDictionary } = globalization?.dictionaries || {};
	const { actionMyOffersPill } = filtersDictionary;

	const { sendInitialSearchEvent, getParamsPersonalizationEvent } = useContext(PersonalizationContext);

	const { user, isAuthenticated } = useUser();

	const [menuState, setMenuState] = useState('close');
	const [isMaxWidth, setIsMaxWidth] = useState(true);
	const [initialLoad, setInitialLoad] = useState(null);
	const [initialTracking, setInitialTracking] = useState();
	const [segmentCDP, setSegmentCDP] = useState([]);
	const [tdLoaded, setTdLoaded] = useState(false);
	const [trackingInfo, setTrackingInfo] = useState();
	const [initEventSent, setInitEventSent] = useState(false);

	const timeline = useRef();
	const wrapperRef = useRef();
	const promoRef = useRef();
	const searchBarRef = useRef();
	const filtersRef = useRef();
	const selectedFiltersRef = useRef();
	const resultsRef = useRef();
	const resultsHeaderRef = useRef();
	const searchCardsRef = useRef();
	const refs = useRef({
		wrapperRef,
		promoRef,
		searchBarRef,
		filtersRef,
		selectedFiltersRef,
		resultsRef,
		searchCardsRef,
		resultsHeaderRef,
	});
	const initialSelection = useRef(null);
	const initialPromoSelection = useRef(null);

	const selectingForCompare = useMemo(() => comparisonViewState === SELECTING, [comparisonViewState]);

	const sendPersonalizationEvent = () => {
		const paramsQuery = { ...query };
		delete paramsQuery.sort;
		const { qualifiers } = paramsQuery;

		const paramsQualifiers = qualifiers ? qualifiers.split('&') : [];

		const request = {
			enableEventTrackng: flags.enableEventTrackng,
			additionalParamsApplied,
			locale,
			searchSelecting,
			qualifiersSelecting: paramsQualifiers,
			query,
			cruiseRecommendationId: results?.cruiseRecommendationId || EMPTY_CRUISE_RECOMMENDATION_ID,
			brand,
		};
		const params = getParamsPersonalizationEvent(request);

		if (params?.data && params?.query) {
			sendInitialSearchEvent(params?.data, params?.query, config);
		}
	};

	useEffect(() => {
		if (flags.enableAemUniversalNavigation) {
			setShowUniversalNavigation(!flags.enableAemUniversalNavigation);
		}
	}, [flags.enableAemUniversalNavigation]);

	useEffect(() => {
		animateIn({ timeline, refs });
		getFavoritesList(user, isAuthenticated);
	}, [user, isAuthenticated]);

	useEffect(() => {
		if (initEventSent) {
			return;
		}

		if (flags.enableEventTrackng && results?.cruises?.length) {
			sendPersonalizationEvent();
			setInitEventSent(true);
		}
	}, [flags.enableEventTrackng, results?.cruises?.length, results?.cruiseRecommendationId]);

	useEffect(() => {
		if (results?.cruises?.length && !initialLoad && ((loadedPromo && flags.showPromotionBanner) || !flags.showPromotionBanner)) {
			const { initialTrackingData, trackingInfoData } = updateTrackingLocal();
			setInitialLoad(TRACKING_ACTION.INIT);
			setInitialTracking(initialTrackingData);
			setTrackingInfo(trackingInfoData);
			updateTracking(initialTrackingData);
		}
	}, [loadedPromo, results, flags.showPromotionBanner, flags.showPromotionBanner]);

	/**
	 * timer to check if window.td script already load
	 */

	const MAX_ATTEMPTS = 20;
	let attempts = 0;
	useEffect(() => {
		const onTimer = () => {
			if (window.td) {
				setTdLoaded(true);
				clearInterval(checkUpdatePage);
			}
			attempts++;
			if (attempts >= MAX_ATTEMPTS) {
				clearInterval(checkUpdatePage);
			}
		};
		const checkUpdatePage = setInterval(onTimer, 1000);
		document.addEventListener(UPDATE_RESULTS_EVENT, (event) => {
			updateSearchSelecting('', event.detail?.query?.search, applyFilters);
		});
		document.addEventListener(EVENT_CRUISE_SEARCH_APPLY_FILTERS, () => {
			window.scrollTo(0, 0);
		});
	}, []);

	useEffect(() => {
		if (flags.enableTargetedOffersFeature && flags.showMyOffers && flags.targetedOffersSegments && initialTrackPageEvent && tdLoaded) {
			const cookieRdwId = Cookies.get(RDW_ID.cookie);
			const cookieTd = Cookies.get(COOKIE_TD.cookie);
			window.td?.fetchUserSegments(
				{
					audienceToken: [publicKeys.cookieRwdAudienceToken, publicKeys.cookieTdAudienceToken],
					keys: {
						cp_rwd_id: cookieRdwId,
						td_client_id: cookieTd,
					},
				},
				setSegmentCDP,
			);
		}
	}, [initialTrackPageEvent, tdLoaded]);

	useEffect(() => {
		if (segmentCDP.length) {
			const toggle_segments = flags.targetedOffersSegments?.split('|');
			const segmentCDPValue = segmentCDP.map((e) => e.values).flat();

			let segmentsMatch = false;

			toggle_segments.forEach((segment) => {
				if (segmentCDPValue.includes(segment)) segmentsMatch = true;
			});

			if (!segmentsMatch && targetedOffersPillShow) {
				updateTargetedOfferPill(false);
			}
			const callback = () => updateTargetedOfferPill(true);
			utagShowOfferPill(query, targetedOffersPillClosed, targetedOffersPillShow, segmentsMatch, callback);
		}
	}, [segmentCDP]);

	useEffect(() => {
		const { search, sort } = makeStoreFromRouteQuery(query);
		const splitEx = getSplitEx();
		const departurePortsGroups = filterOptions.find((option) => option.key === DEPARTURE_PORT).groups;
		const riverDeparturePorts = getRiverDeparturePorts(searchApplied.departurePort, departurePortsGroups);

		if (!initialTrackPageEvent && initialLoad === TRACKING_ACTION.INIT) {
			updateFilterChange(false);
			updateInitialTrackPageEvent(true);
			if (flags.enableDataLayerUnification) {
				trackOnLoadCruiseSearch({
					search,
					sort,
					locale,
					results,
					pagination,
					query,
					splitEx,
					labelsSort,
					brand: fullBrandSettings,
					trackingAction: initialLoad,
					loginStatus: isAuthenticated,
					riverDeparturePorts,
				});
			} else {
				utagEvent(brand, initialTracking, trackingInfo);
			}
		}

		if (initialTrackPageEvent && filterChange && initialLoad === TRACKING_ACTION.SEARCH_UPDATE) {
			updateFilterChange(false);
			if (flags.enableDataLayerUnification) {
				trackOnLoadCruiseSearch({
					search,
					sort,
					locale,
					results,
					pagination,
					query,
					splitEx,
					labelsSort,
					brand: fullBrandSettings,
					trackingAction: initialLoad,
					loginStatus: isAuthenticated,
					riverDeparturePorts,
				});
			} else {
				utagEvent(brand, initialTracking, trackingInfo, appliedDateFilter, initialLoad);
			}
		}
	}, [initialTracking]);

	useEffect(() => {
		if (filterChange && initialTrackPageEvent && !loading && !portAdded) {
			const { initialTrackingData, trackingInfoData } = updateTrackingLocal();

			setInitialLoad(TRACKING_ACTION.SEARCH_UPDATE);
			setInitialTracking(initialTrackingData);
			setTrackingInfo(trackingInfoData);
			updateTracking(initialTrackingData);
		}
	}, [loading, portAdded, filterChange]);

	const setSegmentValidatedCookie = () => {
		const conf = { expires: 1 };
		const dateValue = new Date().toISOString();
		createCookie(COOKIE_SEGMENT_VALIDATED, dateValue, conf, false);
	};

	const getSplitEx = () => {
		const DEFAULT_SPLITEX = 'cs:control';
		const RECOMMENDED = 'RECOMMENDED';

		if (brand === BRAND_CEL.code && !flags.enablePercentageRolloutCampaign?.splitEx == DEFAULT_SPLITEX) {
			return '';
		}

		if (flags.enablePercentageRolloutCampaign?.splitEx) {
			return flags.enablePercentageRolloutCampaign.splitEx;
		}
		const { RECOMMENDED_SPLIT_EX } = UTAG_VIEW;

		return defaultSorting?.by === RECOMMENDED ? RECOMMENDED_SPLIT_EX.EXPERIMENT_ACTIVATED : RECOMMENDED_SPLIT_EX.CONTROL_OFF;
	};

	useEffect(() => {
		if (flags.enablePercentageRolloutCampaign?.splitEx) {
			sessionStorage.setItem('splitEx', flags.enablePercentageRolloutCampaign.splitEx);
		}
	}, [flags.enablePercentageRolloutCampaign]);

	const updateTrackingLocal = () => {
		const { search, sort } = makeStoreFromRouteQuery(query);
		const { country, currency, language } = locale;
		const { cruises, total, cruiseRecommendationId } = results;
		const { currentPage, count } = pagination;
		const { perks } = promo;
		let trackingInfo = createInitialPageTrack(
			search,
			{ country, currency, language },
			{ cruises, total },
			{ currentPage, count },
			perks,
			brand,
			sort,
			user,
			isAuthenticated,
			cruiseRecommendationId,
			query,
		);
		const splitEx = getSplitEx();

		const trackingByBrand = {
			[BRAND_RCI.code]: {
				splitEx,
				cruiseSearchQuery: nlUserQuery,
			},
			[BRAND_CEL.code]: {
				...(splitEx && { splitEx }),
			},
		};

		const [sortInfo, by = ''] = sort || [];
		const labelEvent = labelsSort[`${sortInfo}${by}`];
		const trackingSort = labelEvent ? { searchOption: `${CRUISE_SORT}: ${labelEvent}` } : {};

		trackingInfo = {
			...trackingInfo,
			...(trackingByBrand[brand] || {}),
			...trackingSort,
		};

		const initialSortDate =
			flags.enableDefaultSortOption === SAILDATE
				? {
						searchOption: INITIAL_SEARCH_OPTION.SORT_DATE,
					}
				: {};

		const initialTracking = {
			...tracking,
			...trackingInfo,
			...initialSortDate,
		};

		return {
			initialTrackingData: initialTracking,
			trackingInfoData: trackingInfo,
		};
	};

	const onClosePill = () => {
		setSegmentValidatedCookie();
		closeTargetedOffersPill();
	};

	const openFilter = () => {
		setSegmentValidatedCookie();
		utagOfferPillClicked();
		openFilters(filtersMap.qualifiers.key, FILTER_MODE.SINGLE_FILTER);
	};

	const validatedCookie = !!getCookie(COOKIE_SEGMENT_VALIDATED);
	const showTargetedOffersBanners = targetedOffersLogged && flags.enableTargetedOffersFeature && flags.showMyOffers;

	const showPromotionsBanners = promotions && !showTargetedOffersBanners;

	const updateContainer = (isSticky) => {
		setIsMaxWidth(isSticky);
	};

	const renderPricingPanel = useMemo(() => {
		if (!cruiseDetail) {
			return;
		}

		const pricingPanelVariant = flags.enableCruiseDetailsPanelVariant ? CruiseDetailsVariant.Refined : CruiseDetailsVariant.Basic;

		return <CruiseDetailsPanel variant={pricingPanelVariant} />;
	}, [cruiseDetail]);

	const renderForgotCASModal = useMemo(() => {
		const isForgotCASModalOpen = filterOpen ? flags.enableTargetedOffersFeature && filterOpen === filtersMap.qualifiers.key : modalOpen;

		return isForgotCASModalOpen && <ForgotCASModal />;
	}, [filterOpen, modalOpen]);

	const onFilterSelectedCallback = useCallback(
		(selection, updateAppliedSelection) => {
			const filterSelection = _cloneDeep(selection);
			const searchSelectingCallback = () => {
				if (updateAppliedSelection) {
					applyFilters({ removeHomePageQueryParam: true });
				}
			};

			if (filterSelection[PROMO].length > 0) {
				const [promo] = filterSelection[PROMO];
				filterSelection[PROMO] = [];
				filterSelection[CUSTOM] = [];
				if (promo !== promotionSelected) {
					setPromotionSelected(promo);
					getStatePromotions({ displayQualifier: true, promo, toogles: flags });
					updateAdditionalFilters(getQueryFromObject({ promo: [promo] }, '&', '='));
				}
			} else {
				setPromotionSelected(null);
				setPromotions({ valid: false });
				updateAdditionalFilters(null);
			}
			for (let filter of Object.keys(filterSelection)) {
				updateSearchSelectingFilter(filter, filterSelection[filter], searchSelectingCallback);
			}
		},
		[updateSearchSelectingFilter, applyFilters, promotionSelected],
	);

	const onAppliedSelectionCallback = useCallback(
		(isUserInteraction) => {
			if (promotionApplied !== promotionSelected) {
				setPromotionApplied(promotionSelected);
			}

			applyFilters({ removeHomePageQueryParam: isUserInteraction });
		},
		[applyFilters, promotionSelected, setPromotionSelected],
	);

	const onResetSelectionCallback = useCallback(() => {
		resetSearchSelectingToSearchApplied();
	}, [resetSearchSelectingToSearchApplied]);

	useEffect(() => {
		if (initialSelection.current === null) {
			initialSelection.current = searchApplied;
		}

		if (nlsPerformed) {
			initialSelection.current = currentNls;
		}
	}, [searchApplied, nlsPerformed, currentNls]);

	useEffect(() => {
		if (initialPromoSelection.current === null && promotionApplied) {
			initialPromoSelection.current = {
				[PROMO]: [promotionApplied],
			};
		}
	}, [promotionApplied]);

	return (
		<>
			{process.browser && flags.showMarkerIoTab && <MarkerIO />}
			<PageBackground ariaIgnore />
			{showUniversalNavigation && (
				<>
					{flags.showAlertsOnHeader && <Alerts />}
					<HeaderBackground>
						<ToolBarContainer>
							<HeaderConstrain>
								<ToolBar />
							</HeaderConstrain>
						</ToolBarContainer>
						<HeaderConstrain>
							<MainMenu onClick={() => setMenuState('open')} />
						</HeaderConstrain>
					</HeaderBackground>
				</>
			)}
			<PageConstrain ref={wrapperRef} isMaxWidth={isMaxWidth} className={selectingForCompare ? 'compare' : ''}>
				<PromoWrapper id={PROMO_WRAPPER}>
					<Promo passedRef={promoRef} />
				</PromoWrapper>
				{flags.showSearchBar && (
					<SearchBarConstrain id={SEARCH_BAR}>
						<SearchBar
							passedRef={searchBarRef}
							searchesAllowing={searchesAllowing}
							scrollerId={DEFAULT}
							placeholder={inputOpenSearchBarPlaceholder}
							applyFiltersOnSelect
						/>
					</SearchBarConstrain>
				)}
				{flags.enableNaturalLanguageSearch && (
					<SearchBarConstrain id={SEARCH_BAR}>
						<NLSearch />
					</SearchBarConstrain>
				)}
				{flags.enableFindACruiseWidget ? (
					<FiltersProvider
						onUpdateSelection={onFilterSelectedCallback}
						onAppliedSelection={onAppliedSelectionCallback}
						onResetSelection={onResetSelectionCallback}
						initialSelection={initialSelection.current}
						initialPromoSelection={initialPromoSelection.current}
						externalSelection={currentNls}
						promotionData={promotions}
						locale={locale}
						brandCode={brand}
						featureFlags={flags}
					>
						<CruiseSearchWidget
							containerId={FILTERS_WRAPPER}
							navigationFilters={flags?.showInOrderThePillsOfTheFilters?.split('|') || DEFAULT_FILTER_OPTIONS}
							isSticky={true}
							variant={Variant.Base}
							customElements={cruiseSearchWidgetCustomElements}
							enableUniqueFilters={true}
							enableNumberOfGuests={true}
						/>
					</FiltersProvider>
				) : (
					<Filters searchFilters={DEFAULT} updateContainer={updateContainer} />
				)}{' '}
				{flags.enableFindACruiseWidget ? <></> : <SelectedFilters passedRef={selectedFiltersRef} />}
				{(showTargetedOffersBanners || showPromotionsBanners) && <PromoBanner />}
				<CruisePulseWrapper>
					<Results passedRefs={refs} />
					{renderPricingPanel}
				</CruisePulseWrapper>
				{renderForgotCASModal}
				<Loader />
				{filterOpen && <FiltersModal />}
				{showUniversalNavigation && <SideBarMenu state={menuState} onClick={(state) => setMenuState(state)} />}
			</PageConstrain>
			{!validatedCookie && targetedOffersPillShow && !targetedOffersLogged && !filterOpen && (
				<PillButton
					className={SELECTED_FILTER}
					onClick={(e) => {
						e.preventDefault();
						openFilter();
					}}
					onClose={onClosePill}
					specialColor
					selected
					showX
					isToast
				>
					{actionMyOffersPill}
				</PillButton>
			)}
			{showUniversalNavigation && <Footer />}
		</>
	);
};

SearchPage.propTypes = {
	router: PropTypes.shape({
		query: PropTypes.any,
	}),
};

export default withRouter(SearchPage);
